import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
// Fetch active grading parameters
export const fetchActiveGradingParameters = createAsyncThunk('grading/fetchActiveParameters', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/grading-parameters/active');
        return response.data.data;
    }
    catch (error) {
        // Gracefully handle 404 - grading parameters are optional during onboarding
        if (error.response?.status === 404) {
            console.info('Grading parameters not configured yet - this is expected during initial setup');
            return null; // Return null instead of rejecting
        }
        // Gracefully handle 403 - endpoint requires admin/tutor, students get null
        if (error.response?.status === 403) {
            return null;
        }
        // 401: auth not ready or token rejected — same as "no params" for this optional tutor fetch (avoids error spam during session warmup)
        if (error.response?.status === 401) {
            return null;
        }
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch active grading parameters');
    }
});
// Fetch all grading parameters (admin only)
export const fetchAllGradingParameters = createAsyncThunk('grading/fetchAllParameters', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/grading-parameters');
        return response.data.data;
    }
    catch (error) {
        // Gracefully handle 404 - grading parameters are optional
        if (error.response?.status === 404) {
            console.info('Grading parameters not configured yet - this is expected during initial setup');
            return []; // Return empty array instead of rejecting
        }
        // Gracefully handle 403 - admin-only endpoint, non-admins get empty array
        if (error.response?.status === 403) {
            return [];
        }
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch grading parameters');
    }
});
// Create new grading parameters (admin only)
export const createGradingParameters = createAsyncThunk('grading/createParameters', async (parametersData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/grading-parameters', parametersData);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to create grading parameters');
    }
});
// Update grading parameters (admin only)
export const updateGradingParameters = createAsyncThunk('grading/updateParameters', async ({ id, data }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/grading-parameters/${id}`, data);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to update grading parameters');
    }
});
// Regrade a quiz submission
export const regradeSubmission = createAsyncThunk('grading/regradeSubmission', async ({ submissionId, regradeData }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/quiz-submissions/${submissionId}/regrade`, regradeData);
        return response.data.data.regradeLog;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to regrade submission');
    }
});
const initialState = {
    activeParameters: null,
    allParameters: [],
    regradeLogs: [],
    loading: false,
    error: null
};
const gradingSlice = createSlice({
    name: 'grading',
    initialState,
    reducers: {
        clearError(state) {
            state.error = null;
        },
        resetState(state) {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchActiveGradingParameters
            .addCase(fetchActiveGradingParameters.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchActiveGradingParameters.fulfilled, (state, action) => {
            state.loading = false;
            state.activeParameters = action.payload;
            state.error = null;
        })
            .addCase(fetchActiveGradingParameters.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Failed to fetch active grading parameters';
        })
            // fetchAllGradingParameters
            .addCase(fetchAllGradingParameters.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchAllGradingParameters.fulfilled, (state, action) => {
            state.loading = false;
            state.allParameters = action.payload;
            state.error = null;
        })
            .addCase(fetchAllGradingParameters.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Failed to fetch all grading parameters';
        })
            // createGradingParameters
            .addCase(createGradingParameters.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(createGradingParameters.fulfilled, (state, action) => {
            state.loading = false;
            // If the new parameter is active, it becomes the active one
            if (action.payload.active) {
                state.activeParameters = action.payload;
            }
            state.allParameters.unshift(action.payload);
            state.error = null;
        })
            .addCase(createGradingParameters.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Failed to create grading parameters';
        })
            // updateGradingParameters
            .addCase(updateGradingParameters.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(updateGradingParameters.fulfilled, (state, action) => {
            state.loading = false;
            // If the updated parameter is active, it becomes the active one
            if (action.payload.active) {
                state.activeParameters = action.payload;
            }
            else if (state.activeParameters &&
                state.activeParameters.id === action.payload.id) {
                // If this was previously the active parameter but is no longer active
                state.activeParameters = null;
            }
            // Update in the all parameters array
            state.allParameters = state.allParameters.map(param => param.id === action.payload.id ? action.payload : param);
            state.error = null;
        })
            .addCase(updateGradingParameters.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Failed to update grading parameters';
        })
            // regradeSubmission
            .addCase(regradeSubmission.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(regradeSubmission.fulfilled, (state, action) => {
            state.loading = false;
            state.regradeLogs.unshift(action.payload);
            state.error = null;
        })
            .addCase(regradeSubmission.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Failed to regrade submission';
        });
    }
});
export const { clearError, resetState } = gradingSlice.actions;
export default gradingSlice.reducer;
